import { useColorMode, useColorModeValue } from "@chakra-ui/react";
import { getColor } from "../../../../brandConfig";

/**
 * The calendars look (POLISH v1, 2026-09-25 — copied from governance's kit, which stays governance's).
 * Mac-like surfaces, soft depth, the brand's primary→secondary as the one accent. Every colour comes
 * from the tenant branding record (`getColor`) or Chakra's palette — never a raw hex — so a rebrand
 * re-skins the calendar with the rest of the site, in light and dark.
 *
 * `grid*` tokens are the FullCalendar surface (header row, lines, today, time labels): one place, so
 * light and dark can never drift apart the way the old hard-coded `#0f172a` header text did.
 *
 * FRIENDLY FOR EVERY AGE (Tom, 2026-09-12 — "make it baby boomer friendly"): body text is 16px (`md`),
 * nothing a person needs is smaller than 14px (`sm`) or in tiny capitals; secondary text keeps real
 * contrast (`muted`/`faint` below); buttons are at least 44px tall with words, not icons alone; the main
 * action on a page is the biggest thing on it; anything important is written on the page — a tooltip
 * may add detail but never carries the only copy of it.
 */
export function useCalTheme() {
  const { colorMode } = useColorMode();
  const primary = getColor("primary", colorMode);
  const secondary = getColor("secondary", colorMode);
  const success = getColor("status.success", colorMode);
  const warning = getColor("status.warning", colorMode);
  const danger = getColor("status.error", colorMode);
  const purple = getColor("purpleAccent", colorMode);
  const neutral = getColor("text.muted", colorMode);
  return {
    colorMode,
    primary, secondary, success, warning, danger, purple, neutral,
    accentGradient: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
    warmGradient: `linear-gradient(135deg, ${warning} 0%, ${danger} 100%)`,
    successGradient: `linear-gradient(135deg, ${success} 0%, ${secondary} 100%)`,
    surface: useColorModeValue("white", "whiteAlpha.50"),
    surfaceHover: useColorModeValue("gray.50", "whiteAlpha.100"),
    raised: useColorModeValue("gray.50", "whiteAlpha.100"),
    border: useColorModeValue("blackAlpha.100", "whiteAlpha.100"),
    borderStrong: useColorModeValue("blackAlpha.200", "whiteAlpha.200"),
    text: useColorModeValue("gray.900", "whiteAlpha.900"),
    muted: useColorModeValue("gray.700", "whiteAlpha.800"),
    faint: useColorModeValue("gray.600", "whiteAlpha.700"),
    shadow: useColorModeValue("0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)", "0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 32px rgba(0,0,0,0.45)"),
    shadowHover: useColorModeValue("0 2px 4px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.10)", "0 1px 0 rgba(255,255,255,0.06) inset, 0 20px 48px rgba(0,0,0,0.6)"),
    glass: "saturate(180%) blur(20px)",
    // FullCalendar grid
    gridBg: useColorModeValue("white", "whiteAlpha.50"),
    gridHeaderBg: useColorModeValue("gray.50", "whiteAlpha.100"),
    gridHeaderText: useColorModeValue("gray.800", "whiteAlpha.900"),
    gridLine: useColorModeValue("blackAlpha.200", "whiteAlpha.200"),
    gridLineSoft: useColorModeValue("blackAlpha.100", "whiteAlpha.100"),
    gridAxisText: useColorModeValue("gray.600", "whiteAlpha.800"),
    gridToday: alpha(primary, colorMode === "dark" ? 0.14 : 0.07),
    gridNight: useColorModeValue("blackAlpha.50", "blackAlpha.300"),
    radius: "2xl",
  };
}

export type CalTheme = ReturnType<typeof useCalTheme>;

/** `color` at `amount` (0–1) opacity. Works for any CSS colour (hex, rgb, rgba) via color-mix. */
export const alpha = (color: string, amount: number) => `color-mix(in srgb, ${color} ${Math.round(amount * 100)}%, transparent)`;
